import React, { useEffect, useState, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { Typography, Link, Divider, Grid, Box, Chip, Button } from "@material-ui/core";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useHistory, Link as RouterLink } from "react-router-dom";
import { getCourseById } from "../../../actions/courses";
import { capitalizeMe, colorMe, scrollToMe, extractIdFromSlug } from "../../../helper";
import { RESET_COURSE } from "../../../constants/actionTypes";
import useStyles from "../styles";
import CourseMap from "./CourseMap";
import CourseCommentSection from "./CourseCommentSection";
import CoursePicturesSection from "./CoursePicturesSection";
import CourseRoundsSection from "./CourseRoundsSection";
import CourseDetailsSkeleton from "./CourseDetailsSkeleton";
import StarRating from "./StarRating";
import GoogleAd from "../../GoogleAd/GoogleAd";

const CourseDetails = () => {
  const { course, isLoading } = useSelector((state) => state.courses);
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const { id: slugParam } = useParams();
  const id = extractIdFromSlug(slugParam);
  const courseRoundsRef = useRef();
  const [refreshKey, setRefreshKey] = useState(0);
  const [hasFetched, setHasFetched] = useState(false);
  const user = JSON.parse(localStorage.getItem("profile"));
  const { name = '', phone = '', website = '', city = '', state = '', zip = '', country = '', holes = '', address = '', scorecard = [], teeBoxes = [], courseRating = null, slopeRating = null, greenGrass = '', fairwayGrass = '', courseDescription = '', cityDescription = '', funFact = '', whatToExpect = '', nearbyAttractions = '' } = course;

  useEffect(() => {
    setHasFetched(false);
    // Clear stale course data so the previous course never flashes on screen
    dispatch({ type: RESET_COURSE, payload: [] });
    Promise.resolve(dispatch(getCourseById(id))).finally(() => setHasFetched(true));
    scrollToMe('.MuiContainer-root')
  }, [dispatch, id]);

  const handleScoreLogged = () => {
    // Force CourseRoundsSection to refresh
    setRefreshKey(prev => prev + 1);
  };

  let frontNineCard = []
  let backNineCard = []

  let splitScorecard = () => {
    if (scorecard.length > 0) {
      let nineHoles = scorecard.length === 9

      if (!nineHoles) {
        var splitScorecard = Math.ceil(scorecard.length / 2)
        frontNineCard = scorecard.slice(0, splitScorecard)
        backNineCard = scorecard.slice(splitScorecard, scorecard.length)
      } else {
        frontNineCard = scorecard
      }
    }
  }
  splitScorecard();

  const scorecardDetails = () => {
    if (scorecard.length > 0) {
      let scorecardHoles = Object.keys(scorecard[0]).map(x => x === 'Hole' && x)
      let handicap = Object.keys(scorecard[0]).map(x => x === 'Handicap' && x)
      let par = Object.keys(scorecard[0]).map(x => x === 'Par' && x)
      let nineHoles = scorecard.length === 9

      let fakePlayers = Array.from({ length: 3 }, (x, i) => (
        <TableRow key={i} className={classes.fakePlayers}>
          {frontNineCard.map((x, i) => (
            <TableCell key={i}></TableCell>
          ))}
          <TableCell></TableCell>
          <TableCell></TableCell>
          <TableCell></TableCell>
        </TableRow>
      ))

      if (!nineHoles) {
        var splitScorecard = Math.ceil(scorecard.length / 2)
        frontNineCard = scorecard.slice(0, splitScorecard)
        backNineCard = scorecard.slice(splitScorecard, scorecard.length)
      } else {
        frontNineCard = scorecard
      }

      let holeOutput = (scorecard) => (
        scorecard.map((x, i) => (
          <TableCell key={i} component="th" scope="col" className={classes.scorecardHolesCell}>{x.Hole.toString().toUpperCase()}</TableCell>
        ))
      )
      let handicapOutput = (scorecard) => (
        scorecard.map((x, i) => (
          <TableCell className={classes.scorecardHandicapCell} key={i}>{x.Handicap}</TableCell>
        ))
      )

      let teeOutput = (arr, tee) => (
        arr.map((teeBoxes, i) => (
          Object.values(teeBoxes.tees).map((key, i) => {
            if (tee.color === key.color) {
              return (
                <TableCell style={{ backgroundColor: colorMe('background', tee.color), color: colorMe('font', tee.color) }} key={i}>{key.yards}
                </TableCell>
              )
            }
          })
        ))
      )

      const totalYards = (scorecard) => (
        scorecard.reduce((acc, curr) => {
          Object.values(curr.tees).map((key, index) => {
            if (!acc[key.color]) {
              acc[key.color] = 0
            }
            acc[key.color] += key.yards
          })
          return acc
        }, {})
      )

      let parOutput = (scorecard) => (
        scorecard.map((x, i) => (
          <TableCell className={classes.scorecardParCell} key={i}>{x.Par}</TableCell>
        ))
      )

      let totalPar = (scorecard) => (
        scorecard.reduce((acc, obj) => {
          return acc + obj.Par
        }, 0)
      )

      return (
        <TableContainer className={classes.scorecardTabel}>
          <Typography variant="h6" component="h2" style={{ paddingBottom: '30px' }} >Scorecard</Typography>
          <Table className={classes.table} aria-label="simple table">
            <TableBody>
              <TableRow>
                <TableCell component="th" scope="col" className={classes.scorecardHolesCell}>{scorecardHoles}</TableCell>
                {holeOutput(frontNineCard)}
                {!nineHoles ?
                  <>
                    <TableCell component="th" scope="col" className={classes.scorecardHolesCell}>Out</TableCell>
                    <TableCell component="th" scope="col" className={classes.scorecardHolesCell}>Turn</TableCell>
                  </> :
                  <>
                    <TableCell component="th" scope="col" className={classes.scorecardHolesCell}>In</TableCell>
                    <TableCell component="th" scope="col" className={classes.scorecardHolesCell}>Total</TableCell>
                  </>}
              </TableRow>
              {/* Fill in yardages */}
              {Object.values(frontNineCard[0].tees).map((x, i) => (
                <TableRow key={i}>
                  <TableCell component="th" scope="row" style={{ backgroundColor: colorMe('background', x.color), color: colorMe('font', x.color) }}>
                    {capitalizeMe(x.color)}
                  </TableCell>
                  {teeOutput(frontNineCard, x)}
                  {!nineHoles ?
                    <>
                      <TableCell style={{ backgroundColor: colorMe('background', x.color), color: colorMe('font', x.color) }}>{Object.values(totalYards(frontNineCard))[i]}</TableCell>
                      <TableCell style={{ backgroundColor: colorMe('background', x.color), color: colorMe('font', x.color) }}></TableCell>
                    </>
                    :
                    <>
                      <TableCell style={{ backgroundColor: colorMe('background', x.color), color: colorMe('font', x.color) }}>{Object.values(totalYards(frontNineCard))[i]}</TableCell>
                      <TableCell style={{ backgroundColor: colorMe('background', x.color), color: colorMe('font', x.color) }}>{Object.values(totalYards(scorecard))[i]}</TableCell>
                    </>
                  }

                </TableRow>
              ))}
              <TableRow>
                <TableCell component="th" scope="row" className={classes.scorecardHandicapCell}>{handicap}</TableCell>
                {handicapOutput(frontNineCard)}
                <TableCell className={classes.scorecardHandicapCell}></TableCell>
                <TableCell className={classes.scorecardHandicapCell}></TableCell>
              </TableRow>
              {/* Add empty rows in scorecard */}
              {fakePlayers}
              <TableRow>
                <TableCell component="th" scope="row" className={classes.scorecardParCell}>{par}</TableCell>
                {parOutput(frontNineCard)}
                <TableCell className={classes.scorecardParCell}>{totalPar(frontNineCard)}</TableCell>
                <TableCell className={classes.scorecardParCell}></TableCell>
              </TableRow>
            </TableBody>
          </Table>

          {!nineHoles &&
            <>
              <Divider style={{ margin: "20px 0" }} />
              <Table className={classes.table} aria-label="simple table">
                <TableBody>
                  <TableRow>
                    <TableCell component="th" scope="col" className={classes.scorecardHolesCell}>{scorecardHoles}</TableCell>
                    {holeOutput(backNineCard)}
                    <TableCell component="th" scope="col" className={classes.scorecardHolesCell}>In</TableCell>
                    <TableCell component="th" scope="col" className={classes.scorecardHolesCell}>Total</TableCell>
                  </TableRow>
                  {/* Fill in yardages */}
                  {Object.values(backNineCard[0].tees).map((x, i) => (
                    <TableRow key={i}>
                      <TableCell component="th" scope="row" style={{ backgroundColor: colorMe('background', x.color), color: colorMe('font', x.color) }}>
                        {capitalizeMe(x.color)}
                      </TableCell>
                      {teeOutput(backNineCard, x)}
                      <TableCell style={{ backgroundColor: colorMe('background', x.color), color: colorMe('font', x.color) }}>{Object.values(totalYards(backNineCard))[i]}</TableCell>
                      <TableCell style={{ backgroundColor: colorMe('background', x.color), color: colorMe('font', x.color) }}>{Object.values(totalYards(scorecard))[i]}</TableCell>
                    </TableRow>
                  ))
                  }
                  <TableRow>
                    <TableCell component="th" scope="row" className={classes.scorecardHandicapCell}>{handicap}</TableCell>
                    {handicapOutput(backNineCard)}
                    <TableCell className={classes.scorecardHandicapCell}></TableCell>
                    <TableCell className={classes.scorecardHandicapCell}></TableCell>
                  </TableRow>
                  {fakePlayers}
                  <TableRow>
                    <TableCell component="th" scope="row" className={classes.scorecardParCell}>{par}</TableCell>
                    {parOutput(backNineCard)}
                    <TableCell className={classes.scorecardParCell}>{totalPar(backNineCard)}</TableCell>
                    <TableCell className={classes.scorecardParCell}>{totalPar(scorecard)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </>}
        </TableContainer>
      );
    }
  };

  const locationStr = [city, state].filter(Boolean).join(', ');
  const courseLabel = name && /golf course/i.test(name) ? name : `${name} Golf Course`;
  const metaTitle = name
    ? `${courseLabel} | Scorecard & Details | RYZE Golf`
    : 'Golf Course Details | RYZE Golf';
  const metaDescription = name
    ? `${name}${locationStr ? ' in ' + locationStr : ''}. View the full scorecard, tee boxes${slopeRating ? ', slope rating ' + slopeRating : ''}${courseRating ? ', course rating ' + courseRating : ''}, and more on RYZE Golf.`
    : 'View golf course scorecard, tee boxes, ratings, and details on RYZE Golf.';
  const canonicalUrl = `https://ryzegolf.com/course/${slugParam}`;

  const jsonLd = name ? {
    "@context": "https://schema.org",
    "@type": "SportsActivityLocation",
    "name": name,
    "address": {
      "@type": "PostalAddress",
      ...(address && { "streetAddress": address }),
      ...(city && { "addressLocality": city }),
      ...(state && { "addressRegion": state }),
      ...(zip && { "postalCode": zip }),
      ...(country && { "addressCountry": country }),
    },
    ...(phone && { "telephone": phone }),
    ...(website && { "url": website.includes('http') ? website : 'https://' + website }),
    ...(holes && { "numberOfHoles": parseInt(holes) }),
    "url": canonicalUrl,
  } : null;

  const breadcrumbJsonLd = name ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://ryzegolf.com/" },
      { "@type": "ListItem", "position": 2, "name": "Course Search", "item": "https://ryzegolf.com/courses/search" },
      { "@type": "ListItem", "position": 3, "name": name, "item": canonicalUrl },
    ],
  } : null;

  const courseLoaded = Boolean(course && course._id);

  // Show a skeleton while fetching — never render an empty shell
  if (!courseLoaded && (!hasFetched || isLoading)) {
    return (
      <>
        <Helmet>
          <title>Loading Course... | RYZE Golf</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <CourseDetailsSkeleton />
      </>
    );
  }

  // Course not found (bad ID, deleted course, or API error)
  if (!courseLoaded && hasFetched) {
    return (
      <>
        <Helmet>
          <title>Course Not Found | RYZE Golf</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', minHeight: '400px', gap: '16px' }}>
          <Typography variant='h4' align='center'>Course Not Found</Typography>
          <Typography variant='body1' align='center' color='textSecondary'>
            We couldn't find that course. It may have been removed, or the link may be incorrect.
          </Typography>
          <Button variant='contained' color='primary' onClick={() => history.push('/courses/search')}>
            Search Golf Courses
          </Button>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content="https://ryzegolf.com/logoColor.png" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content="https://ryzegolf.com/logoColor.png" />
        {jsonLd && (
          <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        )}
        {breadcrumbJsonLd && (
          <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
        )}
      </Helmet>
      <Paper
        className={classes.detailsPaper}
        style={{ padding: "20px", borderRadius: "5px" }}
        elevation={6}
      >

        <div className={classes.courseDetailsNav}>
          <Typography align="left">
            <Link
              component={RouterLink}
              aria-label={`link to course search`}
              className={classes.courseLink}
              to="/courses/search">
              Course Search
            </Link>
          </Typography>
          <div>
            <Typography align="right">
              <Link
                component={RouterLink}
                aria-label={`link to edit course '${name}'`}
                className={classes.courseLink}
                to={`/course/update/${id}`}>
                Update Course
              </Link>
            </Typography>
          </div>
        </div>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={4} md={6} lg={6}>
            <Typography variant="h4" component="h1" gutterBottom>{name}</Typography>
            <StarRating course={course} user={user} />
            <Typography>{address}</Typography>
            <Typography>
              {city && city + ","} {state} {zip}
            </Typography>
            <Typography>{country}</Typography>
            <Typography>{phone ? phone.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3') : ''}</Typography>
            {website && (
              <Link
                className={classes.courseLink}
                target="_blank"
                href={`${website.includes("http")
                  ? website
                  : "https://" + website
                  }`}
              >
                {website}
              </Link>
            )}
            {(holes || courseRating || slopeRating || greenGrass || fairwayGrass) && (
              <Box style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '16px' }}>
                {holes && <Chip size="small" label={`${holes} Holes`} />}
                {courseRating && <Chip size="small" label={`Rating: ${courseRating}`} variant="outlined" />}
                {slopeRating && <Chip size="small" label={`Slope: ${slopeRating}`} variant="outlined" />}
                {greenGrass && <Chip size="small" label={`Greens: ${greenGrass}`} variant="outlined" />}
                {fairwayGrass && <Chip size="small" label={`Fairways: ${fairwayGrass}`} variant="outlined" />}
              </Box>
            )}
          </Grid>
          <Grid item xs={12} sm={8} md={6} lg={6} className={classes.courseMapContainer}>
            <CourseMap />
          </Grid>
        </Grid>

        {/* <GoogleAd slot="8882068857" format="horizontal" /> */}

        <Divider style={{ margin: "20px 0 50px" }} />
        {scorecard &&
          <>
            {scorecardDetails()}
          </>}
        <Divider style={{ margin: "50px 0" }} />
        {teeBoxes.length > 0 && (
          <>
            <TableContainer>
              <Typography variant="h6" component="h2" style={{ paddingBottom: '30px' }}>Tee Boxes</Typography>
              <Table className={classes.teeBoxTabel} aria-label="Tee Boxes">
                <TableHead>
                  <TableRow>
                    <TableCell>Tee</TableCell>
                    <TableCell>Slope</TableCell>
                    <TableCell>Handicap</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {teeBoxes
                    .sort(
                      (a, b) =>
                        parseFloat(b.handicap) - parseFloat(a.handicap)
                    )
                    .map((teeBox, i) => (
                      <TableRow key={i}>
                        <TableCell>{capitalizeMe(teeBox.tee)}</TableCell>
                        <TableCell>{teeBox.slope}</TableCell>
                        <TableCell>{teeBox.handicap}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Divider style={{ margin: "50px 0" }} />
          </>
        )}
        {courseDescription && (
          <Box style={{ borderLeft: '4px solid #4caf50', paddingLeft: '16px', margin: '0 0 30px' }}>
            <Typography variant="h6" gutterBottom>About the Course</Typography>
            <Typography paragraph style={{ color: 'inherit', opacity: 0.87 }}>{courseDescription}</Typography>
          </Box>
        )}
        {cityDescription && (
          <Box style={{ borderLeft: '4px solid #2196f3', paddingLeft: '16px', margin: '0 0 30px' }}>
            <Typography variant="h6" gutterBottom>About {city || 'the Area'}</Typography>
            <Typography paragraph style={{ color: 'inherit', opacity: 0.87 }}>{cityDescription}</Typography>
          </Box>
        )}
        {whatToExpect && (
          <Box style={{ borderLeft: '4px solid #ff9800', paddingLeft: '16px', margin: '0 0 30px' }}>
            <Typography variant="h6" gutterBottom>What to Expect</Typography>
            <Typography paragraph style={{ color: 'inherit', opacity: 0.87 }}>{whatToExpect}</Typography>
          </Box>
        )}
        {nearbyAttractions && (
          <Box style={{ borderLeft: '4px solid #9c27b0', paddingLeft: '16px', margin: '0 0 30px' }}>
            <Typography variant="h6" gutterBottom>Nearby Attractions</Typography>
            <Typography paragraph style={{ color: 'inherit', opacity: 0.87 }}>{nearbyAttractions}</Typography>
          </Box>
        )}
        {funFact && (
          <Box style={{ background: 'rgba(255,193,7,0.1)', border: '1px solid rgba(255,193,7,0.4)', borderRadius: '8px', padding: '16px 20px', margin: '0 0 30px' }}>
            <Typography variant="h6" gutterBottom>Fun Fact</Typography>
            <Typography paragraph style={{ fontStyle: 'italic', margin: 0 }}>{funFact}</Typography>
          </Box>
        )}
        {(courseDescription || cityDescription || whatToExpect || nearbyAttractions || funFact) && (
          <Divider style={{ margin: '0 0 30px' }} />
        )}
        <TableContainer>
          <CourseRoundsSection key={refreshKey} course={course} onScoreLogged={handleScoreLogged} user={user} />
        </TableContainer>
        <TableContainer>
          <CoursePicturesSection course={course} />
        </TableContainer>
        <TableContainer>
          <CourseCommentSection course={course} />
        </TableContainer>
      </Paper>
    </>
  );
};

export default CourseDetails;
